import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import DtvText, { IDtvTextProps } from '@directv-digital/dds.ui.dtv-text';
import { AccordionRows } from './components/AccordionRows';
import { channelColWidthStyle, channelLabelColWidthStyle, pkgStyles } from './PackageCmprGrid.styles';
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { defaultSpacing } from '../../customer-acq-compare-tool.styles';
import Button from "@directv-digital/fusion.atoms.button";
import ChipTray from "./components/ChipTray";
import { AddMaximizeIcon } from '@directv-digital/fusion.atoms.icons';
import dynamic from 'next/dynamic';
import type { DtvSnackBarProps } from 'src/components/video-lab/dtv-snackbar';

const DtvSnackBar = dynamic<DtvSnackBarProps>(() => import('src/components/video-lab/dtv-snackbar'), { ssr: false });
import FusionTheme from "@directv-digital/fusion.themes.fusion/";
import { GenrePackHeader } from './components/GenrePackHeader';
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { fireGa4ItemList, fireTabHandleChange, fireVideoPlayerGA } from "@directv-digital/dds.services.analytics-service";
import { IGenreSvodEpochOffers } from 'lib/types/global';
import { buildGenreConflictGraph, normalizeGenreKey } from '../../utils/buildGenreConflictGraph';

const bps = DtvTheme.breakpoints;

// Shared fallback for the optional array props. A `= []` destructure default
// allocates a fresh array every render, so the memos keyed on these props would
// recompute every pass whenever the prop is absent. Never mutated — the render
// paths that sort/filter it always .slice() first.
const EMPTY_PACKAGES: any[] = [];

/**
 * Fallback eviction copy, used when the CMS has not authored `snackBarMessage`.
 * The snackbar already ships in production off the retired
 * `genreConflictRules[].message`, so defaulting here is what keeps the copy from
 * regressing to silence in the window before CI authors the new field. Mirrors
 * the `columnConfig?.snackbarDurationMs ?? 4000` pattern used for the timings.
 * Placeholders are the same two the CMS template uses.
 */
const DEFAULT_CONFLICT_TEMPLATE = "We've replaced [replace_package_name] with [package_name]";

interface PackageCmprGridProps {
    id?: string;
    accordionsData?: Array<any>;
    displayPackages?: any;
    setDisplayPackages?: React.Dispatch<React.SetStateAction<any[]>>;
    activePackagesByColumn?: any;
    myfreeDtvDescription?: {
        content?: string;
    };
    genrePackages?: any;
    packagesConfig?: Array<any>;
    priceMap?: Record<string, any>;
    changeColumnTray?: any;
    miniPackages?: any;
    columnSelections: Record<number, { genre: string[], signature: string[] }>;
    setColumnSelections: React.Dispatch<React.SetStateAction<Record<number, { genre: string[], signature: string[] }>>>;
    miniSelectionsByColumn: Record<number, string[]>;
    setMiniSelectionsByColumn: React.Dispatch<React.SetStateAction<Record<number, string[]>>>;
    snackBarMessage?: {
        // Every other copy prop on this blok is `IDtvTextProps` (`{ content }`),
        // and the adapter forwards the blok verbatim with no normalisation, so a
        // plain text field and a nested text blok both reach us. Tolerate both.
        message?: string | { content?: string };
    };
    columnConfig?: {
        columnLabel?: IDtvTextProps;
        compareLabel?: IDtvTextProps;
        changeColumnLabel?: IDtvTextProps;
        addMorePackLabel?: IDtvTextProps;
        cta?: {
            change?: {label : string};
            signup?: {label : string, signature: any, genreTargetUrl: any, stream?: any};
            close?: {label : string};
        };
        snackbarDurationMs?: number;
        snackbarGapMs?: number;
    };
    maxColumns?: string | number;
    multipackages?: Array<any>;
    type?: string;
    getOffersGenreData?: IGenreSvodEpochOffers;
}

// Generated by Copilot- Start - 2026-04-16 20:55:11
export const PackageCmprGrid = (props: PackageCmprGridProps) => {
  try {
    const isSignaturePack = pkg => pkg?.type === 'internet';
    const isGenrePack = pkg => !isSignaturePack(pkg) && (pkg?.type === 'genre');
    const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, accordionsData, displayPackages, myfreeDtvDescription, genrePackages = EMPTY_PACKAGES, packagesConfig = EMPTY_PACKAGES, priceMap = {}, columnConfig = {}, miniSelectionsByColumn, setMiniSelectionsByColumn, maxColumns, snackBarMessage } = props;
    const packagesLength = useMemo(() => displayPackages?.length, [displayPackages]);
    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const [_hasScrolled, setHasScrolled] = useState<boolean>(false);
    const [device, setDevice] = useState<'mobile' | 'tablet' | 'desktop'>(() => {
      if (typeof window !== 'undefined') {
        if (window.innerWidth < bps.values.sm) {
          return 'mobile';
        }
        if (window.innerWidth < bps.values.md) {
          return 'tablet';
        }
      }
      return 'desktop';
    });

    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth < bps.values.sm) {
          setDevice('mobile');
        } else if (window.innerWidth < bps.values.md) {
          setDevice('tablet');
        } else {
          setDevice('desktop');
        }
      };
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    const { columnSelections, setColumnSelections } = props;
    const drawerRef = useRef<HTMLDivElement>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerColIdx, setDrawerColIdx] = useState<number | null>(null);
    const [_drawerType, setDrawerType] = useState<'genre' | 'signature' | null>(null);
    // Generated by Copilot- Start - 2026-04-16 20:55:11
    // Conflict snackbar — message queue so rapid conflicts each display for their full 4s
    const genreTrayHeaderRef = useRef<HTMLDivElement | null>(null);
    const [conflictSnackbarMessage, setConflictSnackbarMessage] = useState<string>('');
    const [conflictSnackbarOpen, setConflictSnackbarOpen] = useState<boolean>(false);
    const [conflictSnackbarTopPx, setConflictSnackbarTopPx] = useState<number>(200);
    const snackbarQueueRef = useRef<Array<{ message: string; topPx: number }>>([]);
    const snackbarDrainingRef = useRef<boolean>(false);
    const conflictTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Generated by Copilot- End - 2026-04-16 20:55:11
    // Generated by Copilot- Start - 2026-02-24 20:20:06
    const [scrollTrigger, setScrollTrigger] = useState<number>(0);

    // Centralized handler that opens the drawer and always triggers a scroll
    const openDrawer = useCallback((colIdx: number, type: 'genre' | 'signature') => {
      setDrawerColIdx(colIdx);
      setDrawerType(type);
      setDrawerOpen(true);
      setScrollTrigger(prev => prev + 1);
    }, []);

    useEffect(() => {
      if (!drawerOpen || scrollTrigger === 0) {
        return;
      }
      const raf = requestAnimationFrame(() => {
        const stickyHeader = document.getElementById("pkg-table-head");
        const stickyHeight = stickyHeader?.getBoundingClientRect()?.height || 0;
        if (drawerRef.current) {
          const drawerTop = drawerRef.current.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: drawerTop - stickyHeight,
            behavior: 'smooth'
          });
        }
      });
      return () => cancelAnimationFrame(raf);
    }, [scrollTrigger, drawerOpen]);
    // Generated by Copilot- End - 2026-02-24 20:20:06

    useEffect(() => {
      if (!displayPackages) {
        return;
      }
      setColumnSelections(prev => {
        const newSelections: Record<number, { genre: string[], signature: string[] }> = {};
        let changed = false;
        displayPackages.forEach((pkg, idx) => {
          const pkgId = pkg.id;
          if (!pkgId) {
            return;
          }
          if (pkg.type === 'genre') {
            if (prev[idx]?.genre?.length > 0) {
              newSelections[idx] = prev[idx];
            } else {
              newSelections[idx] = {
                genre: [pkgId],
                signature: []
              };
              changed = true;
            }
          } else if (pkg.type === 'internet') {
            if (prev[idx]?.signature?.length > 0) {
              newSelections[idx] = prev[idx];
            } else {
              newSelections[idx] = {
                genre: [],
                signature: [pkgId]
              };
              changed = true;
            }
          }
        });
        if (!changed && Object.keys(newSelections).length === Object.keys(prev).length) {
          return prev;
        }
        return newSelections;
      });
    }, [displayPackages, setColumnSelections]);

    let columns = [...(displayPackages || [])];

    if (columns.length === 1 && isSignaturePack(columns[0])) {
      columns = [
        { isEmpty: true },
        columns[0],
        { isEmpty: true }
      ];
    } else {
      // eslint-disable-next-line no-underscore-dangle
      const _parsed = typeof maxColumns === 'string' ? parseInt(maxColumns, 10) : maxColumns;
      const maxCols = Number.isFinite(_parsed) && _parsed > 0 ? _parsed : 3;
      while (columns.length < maxCols) {
        columns.push({ isCompare: true });
      }
      columns = columns.slice(0, maxCols);
    }

    /* start-copilot */
    // Null-tolerant on purpose: besides chip ids this also normalizes raw Epoch
    // tokens and optional CMS fields in genreConflictGraph, any of which can be
    // undefined. Behaviour for a non-empty string is unchanged.
    // Single implementation shared with the conflict-graph builder — two copies
    // of this normalisation would drift and silently stop resolving tokens.
    const normalizeKey = useCallback((key: any) => normalizeGenreKey(key), []);

    function getTrayHeading(changeColumnTray, type) {
      if (!Array.isArray(changeColumnTray)) {
        return {};
      }
      const tray = changeColumnTray.find(item => item.type === type);
      return {
        title: tray?.title || '',
        selectionHint: tray?.selectionHint || '',
        description: tray?.description || '',
        tooltip: tray?.tooltip || '',
        styles: tray?.styles || {},
        ribbon: tray?.ribbon
      };
    }

    // Memoize tray headings
    const signatureTrayHeading = useMemo(() => getTrayHeading(props.changeColumnTray, 'internet'), [props.changeColumnTray]);
    const genreTrayHeading = useMemo(() => getTrayHeading(props.changeColumnTray, 'genre'), [props.changeColumnTray]);
    const miniPackTrayHeading = useMemo(() => getTrayHeading(props.changeColumnTray, 'genre-mini'), [props.changeColumnTray]);
    // Multi Pack heading: CMS authors configure a changeColumnTray item with type 'genre-multi'
    // to provide the heading label/description for the Multi Pack section.
    const multiPackTrayHeading = useMemo(() => getTrayHeading(props.changeColumnTray, 'genre-multi'), [props.changeColumnTray]);

    const handleSignatureChipClick = useCallback((columnIdx: number, pkgId: string) => {
      setColumnSelections(prev => {
        const prevCol = prev[columnIdx] || {
          genre: [],
          signature: []
        };
        const alreadySelected = prevCol.signature.includes(pkgId);

        // Prepare new selections and new displayPackages together
        setMiniSelectionsByColumn(prevMini => ({
          ...prevMini,
          [columnIdx]: []
        }));

        if (props.setDisplayPackages) {
          props.setDisplayPackages(prevPackages => {
            const newPackages = [...prevPackages];
            if (
              prevPackages[columnIdx] &&
                    (prevPackages[columnIdx].id === pkgId || prevPackages[columnIdx].queryparam === pkgId)
            ) {
              // Deselect: clear the package
              newPackages[columnIdx] = {
                id: 'COMPARE_PLACEHOLDER',
                isCompare: true
              };
            } else {
              // Select: set the package
              const sigPkg = props.packagesConfig?.find(pkg => pkg.id === pkgId || pkg.queryparam === pkgId);
              if (sigPkg) {
                newPackages[columnIdx] = sigPkg;
              }
            }
            return newPackages;
          });
        }

        return {
          ...prev,
          [columnIdx]: {
            ...prevCol,
            signature: alreadySelected ? [] : [pkgId],
            genre: []
          }
        };
      });
    }, [columnSelections, setColumnSelections, setMiniSelectionsByColumn, props.setDisplayPackages, props.packagesConfig]);

    // Generated by Copilot- Start - 2026-04-16 20:55:11
    const snackbarDurationMs = columnConfig?.snackbarDurationMs ?? 4000;
    const snackbarGapMs = columnConfig?.snackbarGapMs ?? 350;
    const drainSnackbarQueue = useCallback(() => {
      // Set flag atomically with the guard so no concurrent drain can start
      if (snackbarDrainingRef.current) {
        return;
      }
      snackbarDrainingRef.current = true;
      const next = () => {
        const item = snackbarQueueRef.current.shift();
        if (!item) {
          snackbarDrainingRef.current = false;
          return;
        }
        setConflictSnackbarTopPx(item.topPx);
        setConflictSnackbarMessage(item.message);
        setConflictSnackbarOpen(true);
        fireVideoPlayerGA({
          event: "trackEvent",
          event_name: "displayed_message",
          displayed_message: item.message?.toLowerCase()
        });
        conflictTimeoutRef.current = setTimeout(() => {
          setConflictSnackbarOpen(false);
          // Brief gap between messages so the next one visually enters fresh
          conflictTimeoutRef.current = setTimeout(next, snackbarGapMs);
        }, snackbarDurationMs);
      };
      next();
    }, [snackbarDurationMs, snackbarGapMs]);
    // Generated by Copilot- End - 2026-04-17 14:45:13

    // Clear any pending timers on unmount to prevent state updates on an unmounted component
    useEffect(() => () => {
      if (conflictTimeoutRef.current) {
        clearTimeout(conflictTimeoutRef.current);
      }
    }, []);
    // Generated by Copilot- End - 2026-04-16 20:55:11

    /**
     * Epoch-derived genre conflict graph: pack id → the pack ids it cannot be
     * selected alongside. The derivation lives in `utils/buildGenreConflictGraph`
     * so it is unit-testable on its own and reusable by the sibling agent tool,
     * which already receives the same `getOffersGenreData`.
     */
    const genreConflictGraph = useMemo(
      () => buildGenreConflictGraph({
        getOffersGenreData: props?.getOffersGenreData,
        packagesConfig
      }),
      [props?.getOffersGenreData, packagesConfig]
    );

    /**
     * Pack id → the label its chip shows, so conflict copy names packs exactly as
     * the agent saw them. Mirrors the chip's own `displayLabel || packageName`
     * precedence; the rendered lists are consulted before `packagesConfig` for the
     * same reason. `packageName` tolerates the `{ content }` CMS shape.
     */
    const genrePackageNameById = useMemo(() => {
      const names: Record<string, string> = {};
      [...(genrePackages || []), ...(displayPackages || []), ...(packagesConfig || [])]
        .forEach((pkg: any) => {
          const packageId = pkg?.id || pkg?.packageId;
          const raw = pkg?.displayLabel || pkg?.packageName;
          const label = typeof raw === 'string' ? raw : raw?.content;
          if (packageId && label && !names[packageId]) {
            names[packageId] = label;
          }
        });
      return names;
    }, [genrePackages, displayPackages, packagesConfig]);

    const handleGenreChipClick = useCallback((columnIdx: number, pkgId: string) => {
      const prevGenreIds = columnSelections[columnIdx]?.genre || [];
      // Deselecting is never blocked and never evicts anything, so the conflict
      // graph is only consulted when a pack is being ADDED.
      const isDeselect = prevGenreIds.includes(pkgId);
      const epochConflicts = isDeselect ? [] : (genreConflictGraph[pkgId] || []);
      // Kept separate from the filter so the copy can name exactly what was dropped.
      const removedIds = prevGenreIds.filter(id => epochConflicts.includes(id));
      const newIds = isDeselect ?
        prevGenreIds.filter(id => id !== pkgId) :
        [...prevGenreIds.filter(id => !removedIds.includes(id)), pkgId];

      // Fill the template whenever something was dropped:
      //   [replace_package_name] → the packs removed, in selection order
      //   [package_name]         → the pack just selected
      // Names fall back to the raw id so the sentence is never left with a hole.
      //
      // A truthiness check on the prop is NOT enough to know it is a string: this
      // blok's copy fields arrive as `{ content }` about as often as bare text, and
      // `.replace` on an object throws inside this handler — before
      // setColumnSelections below — which would turn the whole eviction into a
      // no-op rather than just dropping the copy. Unwrap both shapes, then fall
      // back to the built-in template so an unauthored field still gets a message.
      const rawConflictsMessage = snackBarMessage?.message;
      const conflictsMessage =
        (typeof rawConflictsMessage === 'string' ? rawConflictsMessage : rawConflictsMessage?.content) ||
        DEFAULT_CONFLICT_TEMPLATE;
      const nameOf = (packageId: string) => genrePackageNameById[packageId] || packageId;
      // Replacer FUNCTIONS, not strings: a pack name containing `$&`, `$1` or `$$`
      // would otherwise be read as a replacement pattern and mangle the sentence.
      // Global regexes, not string patterns: a string pattern fills only the FIRST
      // occurrence, so a template repeating a placeholder would ship the literal
      // `[package_name]` to the customer and into the GA payload.
      const conflictMessage = removedIds.length > 0 ?
        conflictsMessage
          .replace(/\[replace_package_name\]/g, () => removedIds.map(nameOf).join(', '))
          .replace(/\[package_name\]/g, () => nameOf(pkgId)) :
        null;
      setColumnSelections(prev => {
        const prevCol = prev[columnIdx] || {
          genre: [],
          signature: []
        };
        return {
          ...prev,
          [columnIdx]: {
            ...prevCol,
            genre: newIds,
            signature: []
          }
        };
      });
      if (conflictMessage) {
        const rect = genreTrayHeaderRef.current?.getBoundingClientRect();
        const topPx = rect ? Math.max(Math.round(rect.top) - 8, 80) : 200;
        // Enqueue the message — drainSnackbarQueue will display each one for its full 4s
        snackbarQueueRef.current.push({
          message: conflictMessage,
          topPx
        });
        drainSnackbarQueue();
      }
      return newIds;
    }, [columnSelections, setColumnSelections, drainSnackbarQueue, genreConflictGraph, genrePackageNameById, snackBarMessage]);

    // Helper: get enabled mini pack IDs for selected genre
    const getEnabledMiniPackIds = useCallback(selectedGenrePkg => {
      if (!selectedGenrePkg) {
        return [];
      }
      const genreId = selectedGenrePkg.id || selectedGenrePkg.queryparam;
      if (!genreId || !Array.isArray(packagesConfig)) {
        return [];
      }
      const found = packagesConfig.find(
        pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId) &&
                pkg.type === 'genre'
      );
      if (found && Array.isArray(found.miniPack)) {
        return found.miniPack;
      }
      return [];
    }, [packagesConfig]);

    // Generated by Copilot- Start - 2026-04-08 15:28:21
    const syncMiniAndDisplayForGenre = useCallback((columnIdx: number, genreIds: string[]) => {
      setMiniSelectionsByColumn(prevMini => {
        let newEnabledMiniPackIds: string[] = [];
        if (genreIds.length > 0) {
          newEnabledMiniPackIds = Array.from(new Set(genreIds
            .map(genreId => {
              const genrePkg = genrePackages?.find(pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId)) ||
                            packagesConfig?.find(pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId) && pkg.subType === 'multi');
              return genrePkg ? getEnabledMiniPackIds(genrePkg) : [];
            })
            .flat()));
        }
        const prevSelected = prevMini[columnIdx] || [];
        const filtered = prevSelected.filter(id => newEnabledMiniPackIds.includes(id));
        return {
          ...prevMini,
          [columnIdx]: filtered
        };
      });
      if (props.setDisplayPackages) {
        props.setDisplayPackages(prevPackages => {
          const newPackages = [...prevPackages];
          if (genreIds.length === 0) {
            newPackages[columnIdx] = { isCompare: true };
          } else {
            // Resolve primary package: check regular genres first, then multi packs
            const mainGenrePkg = genrePackages.find(pkg => pkg.id === genreIds[0] || pkg.packageId === genreIds[0] || pkg.queryparam === genreIds[0]) || packagesConfig?.find(pkg => (pkg.id === genreIds[0] || pkg.packageId === genreIds[0] || pkg.queryparam === genreIds[0]) && pkg.subType === 'multi');
            newPackages[columnIdx] = mainGenrePkg;
          }
          return newPackages;
        });
      }
    }, [genrePackages, packagesConfig, getEnabledMiniPackIds, setMiniSelectionsByColumn, props.setDisplayPackages]);
    // Generated by Copilot- End - 2026-04-08 15:28:21
    /* end-copilot */

    const tooltipPopperProps = {
      sx: {
        "& .MuiTypography-root": {
          fontFamily: `roboto-regular`,
          fontSize: "14px"
        }
      }
    };
    /**
     * Wrapper for Fusion Tooltip icon — inherits font-size from the parent
     * DtvText variant so the ⓘ scales naturally with the title text.
     * WAT-10355: Removed InfoCircleIcon (36x36 SVG), uses self-closing Tooltip instead.
     */
    const tooltipIconWrapperSx = {
      display: 'inline-flex',
      alignItems: 'center'
    };
    const TrayHeader = ({ title, tooltip, selectionHint, description, titleVariant = "subhead2medium" }) => {
      if (!title && !description) {
        return null;
      }
      return (
            <>
                {title && (
                    <DtvBox sx={{
 display: 'flex',
alignItems: 'center',
gap: 1
}}>
                        <DtvText variant={titleVariant} content={title} />
                        {tooltip && (
                            <DtvBox sx={tooltipIconWrapperSx}>
                                <Tooltip
                                    content={tooltip}
                                    placement="top"
                                    PopperProps={tooltipPopperProps}
                                />
                            </DtvBox>
                        )}
                        {selectionHint && (
                            <DtvText variant="body3" content={selectionHint} sx={{ ml: 1 }} />
                        )}
                    </DtvBox>
                )}
                {description && (
                    <DtvText variant="body3" content={description} sx={{ mb: 1 }} />
                )}
            </>
      );
    };
    // Generated by Copilot- End - 2026-04-20 13:33:40

    /**
     * renderSignatureTray: Renders signature package chips for the active column
     */
    // Helper to get selected package IDs in other columns
    function getSelectedIdsInOtherColumns(
      columnSelections: Record<number, { signature?: string[]; genre?: string[] }>,
      currentColIdx: number,
      type: 'signature' | 'genre'
    ) {
      const ids: string[] = [];
      Object.entries(columnSelections).forEach(([colIdx, selRaw]) => {
        const sel = selRaw as { signature?: string[]; genre?: string[] };
        if (Number(colIdx) !== currentColIdx) {
          if (type === 'signature') {
            ids.push(...(sel.signature || []));
          }
          if (type === 'genre') {
            ids.push(...(sel.genre || []));
          }
        }
      });
      return ids;
    }
    const renderSignatureTray = useCallback(({ columnIdx, columnSelections, packagesConfig, priceMap }) => {
      const { title, selectionHint, description, tooltip } = signatureTrayHeading;
      // Get signature IDs selected in other columns
      const selectedSignatureIdsInOtherCols = getSelectedIdsInOtherColumns(columnSelections, columnIdx, 'signature');
      if (drawerOpen && drawerColIdx !== null) {
        return (
                <DtvBox sx={{
 mb: 2,
padding: "12px 20px"
}}>
                    <TrayHeader
                        title={title}
                        tooltip={tooltip}
                        selectionHint={selectionHint}
                        description={description}
                    />
                    <DtvBox sx={{
 display: 'flex',
gap: 2,
flexWrap: 'wrap',
mt: 1
}}>
                        {packagesConfig
                            ?.filter(pkg => pkg?.type === 'internet')
                            .slice()
                            .sort((a, b) => {
                                const aOrder = Number(a?.order ?? a?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                                const bOrder = Number(b?.order ?? b?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                                return aOrder - bOrder;
                            })
                            .map(pkg => {
                                const pkgId = pkg.id || pkg.queryparam;
                                const selectedIds = columnSelections[columnIdx]?.signature || [];
                                const normalizedPkgId = normalizeKey(pkgId);
                                const packagePrice = priceMap[normalizedPkgId];
                                const isDisabled = selectedSignatureIdsInOtherCols.includes(pkgId);
                                return (
                                    <ChipTray
                                        key={normalizedPkgId}
                                        pkgName={pkg.displayLabel || pkg.packageName}
                                        price={packagePrice}
                                        channelCount={pkg?.channel?.count}
                                        isSelected={selectedIds.includes(pkgId)}
                                        disabled={isDisabled}
                                        onClick={() => handleSignatureChipClick(columnIdx, pkgId)}
                                    />
                                );
                            })}
                    </DtvBox>
                </DtvBox>
        );
      }
      return null;
    }, [drawerOpen, drawerColIdx, signatureTrayHeading, normalizeKey, handleSignatureChipClick]);

    /**
     * renderGenreTray: Renders genre package chips and mini packs for the active column
     */
    const renderGenreTray = useCallback(({ columnIdx, columnSelections, genrePackages, priceMap, miniPackages, miniSelectionsByColumn, handleMiniPackChipClick, getEnabledMiniPackIds, multiPackages }) => {
      const selectedGenreIds = columnSelections[columnIdx]?.genre || [];
      // Genre allows same selections across columns — no cross-column disable
      let enabledMiniPackIds = [];
      if (selectedGenreIds.length > 0) {
        enabledMiniPackIds = Array.from(new Set(selectedGenreIds
          .map(genreId => {
            const genrePkg = genrePackages?.find(pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId)) ||
                        (multiPackages || []).find(pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId));
            return genrePkg ? getEnabledMiniPackIds(genrePkg) : [];
          })
          .flat()));
      }
      const { title, selectionHint, description, tooltip } = genreTrayHeading;
      const { title: miniPackTitle, selectionHint: miniPackSelectionHint, description: miniPackDescription, tooltip: miniPackTooltip } = miniPackTrayHeading;
      const { title: mpTitle, description: mpDesc, ribbon: mpRibbon, tooltip: mpTooltip } = multiPackTrayHeading;
      return (
            <DtvBox sx={{
 mb: 2,
padding: "12px 20px"
}}>
                <div ref={genreTrayHeaderRef}>
                    <TrayHeader
                        title={title}
                        tooltip={tooltip}
                        selectionHint={selectionHint}
                        description={description}
                    />
                </div>
                <DtvBox sx={{
 display: 'flex',
gap: 2,
flexWrap: 'wrap',
mt: 1
}}>
                    {genrePackages
                        ?.slice()
                        .sort((a, b) => {
                            const aOrder = Number(a?.order ?? a?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                            const bOrder = Number(b?.order ?? b?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                            return aOrder - bOrder;
                        })
                        .map((pkg, _idx) => {
                            const pkgId = pkg.id || pkg.queryparam;
                            const normalizedPkgId = normalizeKey(pkgId);
                            const selectedIds = selectedGenreIds;
                            const packagePrice = priceMap[normalizedPkgId];
                            return (
                                <ChipTray
                                    key={normalizedPkgId}
                                    pkgName={pkg.displayLabel || pkg.packageName}
                                    price={packagePrice}
                                    channelCount={pkg?.channel?.count}
                                    isSelected={selectedIds.includes(pkgId)}
                                    onClick={() => {
                                        const resolvedIds = handleGenreChipClick(columnIdx, pkgId);
                                        syncMiniAndDisplayForGenre(columnIdx, resolvedIds);
                                    }}
                                />
                            );
                        })}
                </DtvBox>
                {/* Generated by Copilot- Start - 2026-04-08 15:28:21 */}
                {(multiPackages || []).length > 0 && (
                    <DtvBox sx={{ mt: "12px" }}>
                        <DtvBox sx={{
 display: 'flex',
alignItems: 'center',
gap: 1,
mb: 0.5
}}>
                            <DtvText variant="body3" content={mpTitle || 'Multi Pack'} />
                            {mpTooltip && (
                                <DtvBox sx={tooltipIconWrapperSx}>
                                    <Tooltip
                                        content={mpTooltip}
                                        placement="top"
                                        PopperProps={tooltipPopperProps}
                                    />
                                </DtvBox>
                            )}
                            {mpRibbon?.content && (
                                <DtvText
                                    variant={mpRibbon.variant || 'smalltextmedium'}
                                    content={mpRibbon.content}
                                    sx={{
                                        backgroundColor: `${mpRibbon.background || '#FFB800'} !important`,
                                        color: `${mpRibbon.color || '#000'} !important`,
                                        padding: '2px 8px !important',
                                        borderRadius: "2px"
                                    }}
                                />
                            )}
                        </DtvBox>
                        {mpDesc && <DtvText variant="body3" content={mpDesc} sx={{ mb: 1 }} />}
                        <DtvBox sx={{
 display: 'flex',
gap: 2,
flexWrap: 'wrap',
mt: '7px'
}}>
                            {(multiPackages || [])
                                .slice()
                                .sort((a, b) => {
                                    const aOrder = Number(a?.order ?? a?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                                    const bOrder = Number(b?.order ?? b?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                                    return aOrder - bOrder;
                                })
                                .map(pkg => {
                                    const pkgId = pkg.id || pkg.queryparam;
                                    const normalizedPkgId = normalizeKey(pkgId);
                                    const packagePrice = priceMap[normalizedPkgId];
                                    return (
                                        <ChipTray
                                            key={normalizedPkgId}
                                            pkgName={pkg.displayLabel || pkg.packageName}
                                            price={packagePrice}
                                            channelCount={pkg?.channel?.count}
                                            isSelected={selectedGenreIds.includes(pkgId)}
                                            onClick={() => {
                                                const resolvedIds = handleGenreChipClick(columnIdx, pkgId);
                                                syncMiniAndDisplayForGenre(columnIdx, resolvedIds);
                                            }}
                                        />
                                    );
                                })}
                        </DtvBox>
                    </DtvBox>
                )}
                {/* Generated by Copilot- End - 2026-04-08 15:28:21 */}
                {/* Mini pack chip tray: Only show for genre pack */}
                <DtvBox sx={{ mt: 3 }}>
                    <TrayHeader
                        title={miniPackTitle}
                        tooltip={miniPackTooltip}
                        selectionHint={miniPackSelectionHint}
                        description={miniPackDescription}
                        titleVariant={"body3"}
                    />
                    <DtvBox sx={{
 display: 'flex',
flexWrap: 'wrap',
gap: 2,
marginTop: "8px"
}}>
                        {miniPackages
                            ?.slice()
                            .sort((a, b) => {
                                const aOrder = Number(a?.order ?? a?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                                const bOrder = Number(b?.order ?? b?.offers?.[0]?.order ?? Number.POSITIVE_INFINITY);
                                return aOrder - bOrder;
                            })
                            .map(miniPack => {
                                const isEnabled = enabledMiniPackIds.includes(miniPack.id);
                                const isSelected = (miniSelectionsByColumn[columnIdx] || []).includes(miniPack.id);
                                const normalizedPkgId = normalizeKey(miniPack.id);
                                const price = priceMap?.[normalizedPkgId] || 0;
                                return (
                                    <ChipTray
                                        key={normalizedPkgId}
                                        pkgName={miniPack.displayLabel}
                                        price={price}
                                        channelCount={miniPack.channel?.displayCount}
                                        isSelected={isSelected}
                                        onClick={() => isEnabled && handleMiniPackChipClick(miniPack.id)}
                                        disabled={!isEnabled}
                                    />
                                );
                            })}
                    </DtvBox>
                </DtvBox>
            </DtvBox>
      );
    }, [genreTrayHeading, miniPackTrayHeading, multiPackTrayHeading, normalizeKey, handleGenreChipClick, syncMiniAndDisplayForGenre]);

    const handleMiniPackChipClick = useCallback(miniPackId => {
      if (drawerColIdx === null) {
        return;
      }
      const selectedGenreIds = columnSelections[drawerColIdx]?.genre || [];
      let enabledMiniPackIds = [];
      if (selectedGenreIds.length > 0) {
        enabledMiniPackIds = Array.from(new Set(selectedGenreIds
          .map(genreId => {
            // Resolve genre or multi pack (subType:'multi') for mini-pack enablement
            const genrePkg = props.genrePackages?.find(pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId)) ||
                        props.packagesConfig?.find(pkg => (pkg.id === genreId || pkg.packageId === genreId || pkg.queryparam === genreId) && pkg.subType === 'multi');
            return genrePkg ? getEnabledMiniPackIds(genrePkg) : [];
          })
          .flat()));
      }
      if (!enabledMiniPackIds.includes(miniPackId)) {
        return;
      }
      setMiniSelectionsByColumn(prev => {
        const prevSelected = prev[drawerColIdx] || [];
        let updated;
        if (prevSelected.includes(miniPackId)) {
          updated = {
            ...prev,
            [drawerColIdx]: prevSelected.filter(id => id !== miniPackId)
          };
        } else {
          updated = {
            ...prev,
            [drawerColIdx]: [...prevSelected, miniPackId]
          };
        }
        return updated;
      });
    }, [miniSelectionsByColumn, drawerColIdx, columnSelections, props.genrePackages, getEnabledMiniPackIds, setMiniSelectionsByColumn]);

    const mainColWidthStyle = useMemo(() => {
      const colCount = packagesLength || 1;
      return {
        flexBasis: `calc((100% - 100px) / ${colCount})`,
        maxWidth: `calc((100% - 100px) / ${colCount})`,
        width: `calc((100% - 100px) / ${colCount})`,
        [bps.up(bps.values.sm)]: {
          flexBasis: `calc((100% - 118px) / ${colCount})`,
          maxWidth: `calc((100% - 118px) / ${colCount})`,
          width: `calc((100% - 118px) / ${colCount})`
        }
      }
    }, [packagesLength]);

    useEffect(() => {
      function onWindowResize() {
        const tableHeaderElem = tableHeaderRef.current || document.getElementById("pkg-table-head");
        if (tableHeaderElem) {
          setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
        }
      };
      let scrollDebounceTimer;
      function onScroll() {
        if (device === 'mobile') {
          setHasScrolled(false);
          return;
        }
        clearTimeout(scrollDebounceTimer);
        scrollDebounceTimer = setTimeout(() => {
          const tableHeaderElem = tableHeaderRef.current || document.getElementById("pkg-table-head");
          if (tableHeaderElem) {
            setHasScrolled(window?.scrollY > tableHeaderElem?.offsetTop);
          }
        }, 100);
      }
      window.addEventListener("resize", onWindowResize);
      window.addEventListener("scroll", onScroll);
      onWindowResize();
      onScroll();
      return () => {
        window.removeEventListener("resize", onWindowResize);
        window.removeEventListener("scroll", onScroll);
        clearTimeout(scrollDebounceTimer);
      }
    }, [device]);

    function getSignupHref(index: number) {
      const colSel = columnSelections[index] || {
        genre: [],
        signature: []
      };
      // --- GENRE ---
      if (colSel.genre && colSel.genre.length > 0) {
        const genreNames = colSel.genre
          .map(id => {
            const pkg = genrePackages.find(p => p.id === id || p.queryparam === id) ||
                            packagesConfig.find(p => (p.id === id || p.queryparam === id) && p.subType === 'multi');
            return pkg?.queryparam;
          })
          .filter(Boolean);

        const miniNames = (miniSelectionsByColumn[index] || [])
          .map(id => {
            const mini = props.miniPackages?.find(m => m.id === id);
            return mini?.queryparam;
          })
          .filter(Boolean);

        const allNames = [...genreNames, ...miniNames];
        const baseUrl = columnConfig?.cta?.signup?.genreTargetUrl || "https://www.directv.com/shop/build/configure";
        const query = allNames?.length > 0 ? `?preConfigItems=${encodeURIComponent(allNames.join(","))}` : "";
        return `${baseUrl}${query}`;
      }

      // --- SIGNATURE ---
      if (colSel.signature && colSel.signature.length > 0) {
        const sigId = colSel.signature[0];
        const sigPkg = packagesConfig.find(p => p.id === sigId || p.queryparam === sigId);
        if (sigPkg) {
          const domain = typeof window !== 'undefined' && window.location?.origin ?
            window.location.origin :
            "https://www.directv.com";
          const sigKey = (sigPkg?.queryparam || "").toLowerCase();
          const streamUrlMap = columnConfig?.cta?.signup?.stream || {};
          const signatureUrlMap = columnConfig?.cta?.signup?.signature || {};
          let url = props?.type === "byod" ?
            streamUrlMap[sigKey] || signatureUrlMap[sigKey] :
            signatureUrlMap[sigKey];
          url = url?.startsWith("/") ? `${domain}${url}` : url;
          return url || "";
        }
      }
      return '';
    }

    return (
        <DtvBox
            sx={{
                ...defaultSpacing || {},
                [bps.down(bps.values.sm)]: {
                    padding: "0px !important",
                    tableLayout: "fixed"
                }
            }}
        >
            <Table id={`${id}-table`} role="presentation"
                sx={{
                    width: "100%",
                    [bps.down(bps.values.sm)]: {
                        ...(packagesLength > 4 && {
                            textWrap: "wrap"
                        })
                    }
                }}
            >
                <TableHead id={`pkg-table-head`}
                    ref={tableHeaderRef}
                    sx={{
                        ...pkgStyles?.pkgTableHead || {}
                    }}>
                    {/* <TableRow> */}
                    <DtvBox component="tr"
                        id={`${id}-PackagesHeader`}
                        sx={{
                            ...pkgStyles.packagesHeaderRow
                        }}>
                        {/* EMPTY HEADER (ROW LABEL COLUMN) */}
                        <DtvBox component="td"
                            id={`${id}-PackagesHeader-1`}
                            sx={{
                                ...pkgStyles.headerCellHeight,
                                ...channelLabelColWidthStyle
                            }}
                        />
                        {columns.map((pkg, index) => {
                            const isGenre = isGenrePack(pkg);
                            return (
                                <DtvBox component="td"
                                    key={pkg.packageId || index}
                                    sx={{
                                        ...pkgStyles.headerCellHeight,
                                        background: pkg.isCompare ? colorPalette.neutral[100] : colorPalette.blue[50],
                                        ...channelColWidthStyle || {},
                                        ...(index === columns.length - 1 && {
                                            mr: {
 xs: "2px",
sm: 0
}
                                        })
                                    }}
                                >
                                    {/* COLUMN WRAPPER */}
                                    <DtvBox
                                        sx={{
                                            ...pkgStyles.columnWrapper,
                                            justifyContent: device === 'mobile' ? (pkg?.isCompare ? 'flex-start' : 'space-between') : 'flex-start'
                                        }}
                                    >
                                        {/* COLUMN TITLE */}
                                        <DtvBox
                                            sx={{
                                                ...pkgStyles.columnTitleBar
                                            }}
                                        >
                                        <DtvText
                                           content={`${columnConfig?.columnLabel?.content || "Column"} ${index + 1}`}
                                           variant={"body3medium"}
                                        />
                                        </DtvBox>
                                        {/* ================= PACKAGE NAME AREA ================= */}
                                        <DtvBox
                                            sx={{
                                                ...pkgStyles.packageNameArea,
                                                mt: pkg.isCompare ? {
 xs: "3px",
sm: 0
} : 0
                                            }}
                                        >
                                            {(() => {
                                                const colSel = columnSelections[index] || {
 genre: [],
signature: []
};
                                                if (colSel.genre && colSel.genre.length > 0) {
                                                    return (
                                                        <GenrePackHeader
                                                            device={device}
                                                            index={index}
                                                            columnSelections={columnSelections}
                                                            genrePackages={props.genrePackages}
                                                            miniSelectionsByColumn={miniSelectionsByColumn}
                                                            miniPackages={props.miniPackages}
                                                            packagesConfig={props.packagesConfig}
                                                            onAddMorePack={() => {
                                                                openDrawer(index, 'genre');
                                                            }}
                                                            addMoreLabel={columnConfig?.addMorePackLabel?.content}
                                                        />
                                                    );
                                                }
                                                if (colSel.signature && colSel.signature.length > 0) {
                                                    const sigId = colSel.signature[0];
                                                    const sigPack = props.packagesConfig?.find(p => (p.id === sigId || p.queryparam === sigId));
                                                    if (sigPack) {
                                                        return (
                                                            <DtvText
                                                                content={sigPack.packageName || sigPack.displayLabel || sigPack.id}
                                                                variant={"body2medium"}
                                                                sx={{
                                                                    ...(device === 'mobile' ? {
                                                                        writingMode: 'vertical-rl',
                                                                        textOrientation: 'sideways',
                                                                        transform: 'rotate(180deg)',
                                                                        '& sup': {
                                                                            verticalAlign: 'baseline',
                                                                            position: 'relative',
                                                                            top: 0,
                                                                            fontSize: '0.7em'
                                                                        }
                                                                    } : {}),
                                                                    fontSize: '16px !important',
                                                                    textAlign: 'center'
                                                                }}
                                                            />
                                                        );
                                                    }
                                                }

                                                if (
                                                    !colSel.genre?.length && !colSel.signature?.length
                                                ) {
                                                    return (
                                                        <DtvText
                                                            content={columnConfig?.compareLabel?.content}
                                                            variant="body2medium"
                                                            sx={{
                                                                ...pkgStyles.compareLabelText
                                                            }}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })()}

                                        </DtvBox>
                                        {/* ================= BUTTON AREA ================= */}
                                        <DtvBox
                                            sx={{
                                                ...pkgStyles.buttonArea,
                                                mt: pkg.isCompare ?
                                                    {
 xs: '26px',
sm: 'auto'
} :
                                                    {
 xs: '24px',
sm: 'auto'
},
                                                flexDirection: {
 xs: 'column',
sm: 'row'
},
                                                justifyContent: {
 xs: 'flex-start',
sm: 'center'
}
                                            }}
                                        >

                                            {!pkg.isCompare ? (
                                                <>
                                                    <Button
                                                        mode={"light"}
                                                        size={"small"}
                                                        buttontype="secondary"
                                                        sx={{
                                                            height: "30px",
                                                            margin: "0",
                                                            fontFamily: "roboto-medium !important",
                                                            fontStyle: "normal",
                                                            fontWeight: 500,
                                                            lineHeight: "130%",
                                                            padding: {
 xs: '0 6px',
sm: '0 16px'
},
                                                            [FusionTheme.breakpoints.up("sm")]: {
                                                                width: "74px"
                                                            },
                                                            [FusionTheme.breakpoints.up("lg")]: {
                                                                width: "101px"
                                                            }
                                                        }}
                                                        onClick={() => {
                                                           // Generated by Copilot- Start - 2026-02-21 20:02:48
                                                            // WAT-9622 AC 3c - Change package CTA
                                                            fireTabHandleChange({
                                                                linkName: "change_package",
                                                                linkPosition: "comparison_page",
                                                                linkDestination: "comparison_tray"
                                                            });
                                                            // Generated by Copilot- End - 2026-02-21 20:02:48
                                                            openDrawer(index, isGenre ? 'genre' : 'signature');
                                                        }}
                                                    >
                                                        {columnConfig?.cta?.change?.label}
                                                    </Button>
                                                    <Button
                                                        buttontype={"primary"}
                                                        size={"small"}
                                                        mode={"light"}
                                                        sx={{
                                                            margin: 0,
                                                            padding: {
 xs: '0 6px',
md: '0 16px'
},
                                                            height: "30px",
                                                            [FusionTheme.breakpoints.up("md")]: {
                                                                width: "74px"
                                                            },
                                                            [FusionTheme.breakpoints.up("lg")]: {
                                                                width: "101px"
                                                            }
                                                        }}
                                                        component="a"
                                                        rel="noopener noreferrer"
                                                        href={getSignupHref(index)}

                                                       onClick={() => {
                                                          // Generated by Copilot- Start - 2026-02-21 20:02:48
                                                            // WAT-9622 AC 3e - Sign up CTA: link_click + select_item
                                                            // const signupHref = getSignupHref(index);
                                                            fireTabHandleChange({
                                                                linkName: "sign_up",
                                                                linkPosition: "comparison_page",
                                                                linkDestination: getSignupHref(index) || window.location.href
                                                            });
                                                            const colSel = columnSelections[index] || {
 genre: [],
signature: []
};
                                                            const isGenrePkg = colSel.genre && colSel.genre.length > 0;
                                                            fireGa4ItemList("select_item", [{
                                                                item_category: isGenrePkg ? "genre_pack" : "signature_package",
                                                                item_list_name: "package_comparison"
                                                            }]);
                                                        }}
                                                     // Generated by Copilot- End - 2026-02-21 20:02:48

                                                    >
                                                        {columnConfig?.cta?.signup?.label || "Sign up"}
                                                    </Button>
                                                    {/* end-copilot */}
                                                </>
                                            ) : (
                                                    <Button
                                                        size="small"
                                                        mode="light"
                                                        iconOnly={true}
                                                        buttontype="secondary"
                                                        aria-label="Add package"
                                                        icon={
                                                            <AddMaximizeIcon fill="currentColor" />
                                                        }
                                                        sx={{
                                                            minWidth: "30px",
                                                            width: "30px",
                                                            maxWidth: "30px",
                                                            height: "30px",
                                                            margin: 0,
                                                            padding: 0
                                                        }}
                                                        onClick={() => {
                                                            // Generated by Copilot- Start - 2026-02-21 20:02:48
                                                            // WAT-9622 AC 3d - Compare another package CTA
                                                            fireTabHandleChange({
                                                                linkName: "compare_another_package",
                                                                linkPosition: "comparison_page",
                                                                linkDestination: "comparison_tray"
                                                            });
                                                            // Generated by Copilot- End - 2026-02-21 20:02:48
                                                            openDrawer(index, 'signature');
                                                        }}
                                                    >
                                                    </Button>

                                            )}
                                        </DtvBox>
                                    </DtvBox>
                                </DtvBox>
                            );
                        })}
                    </DtvBox>
                    {/* </TableRow> */}
                </TableHead>
                <TableBody id={`${id}-table-body`}>
                    {drawerOpen && drawerColIdx !== null && (
                        <DtvBox
                            ref={drawerRef}
                            sx={{
                                ...pkgStyles.drawerContainer
                            }}
                        >
                            {/* Drawer header */}
                            <DtvBox sx={{ ...pkgStyles.drawerHeader }}>
                                <DtvText variant="body1medium" content={`${columnConfig?.changeColumnLabel?.content || "Change column"} ${drawerColIdx + 1}`} sx={{ paddingLeft: "20px" }}></DtvText>
                                <Button size="small" mode="light" onClick={() => setDrawerOpen(false)}>{columnConfig?.cta?.close?.label}</Button>
                            </DtvBox>
                            {(() => {
                                if (!Array.isArray(props.changeColumnTray)) {
 return null;
}
                                // Generated by Copilot- Start - 2026-04-21 14:52:03
                                const normalizedOrigin = props?.type || '';
                                const originTrayOrder = (type: string): number | null => {
                                    if (normalizedOrigin === 'internet' || normalizedOrigin === 'byod') {
 return type === 'internet' ? 0 : 1;
}
                                    if (normalizedOrigin === 'genre') {
 return type === 'genre' ? 0 : 1;
}
                                    return null;
                                };
                                // Generated by Copilot- End - 2026-04-21 14:52:03
                                const trays = props.changeColumnTray
                                    .filter(item => item?.type === 'genre' || item?.type === 'internet')
                                    .slice()
                                    .sort((a, b) => {
                                        const aOriginOrder = originTrayOrder(a.type);
                                        const bOriginOrder = originTrayOrder(b.type);
                                        if (aOriginOrder !== null && bOriginOrder !== null && aOriginOrder !== bOriginOrder) {
                                            return aOriginOrder - bOriginOrder;
                                        }
                                        return (a.order ?? 0) - (b.order ?? 0);
                                    });
                                const genreIdx = trays.findIndex(item => item?.type === 'genre');
                                const sigIdx = trays.findIndex(item => item?.type === 'internet');
                                if (genreIdx !== -1 && sigIdx !== -1) {
                                    return trays.map((item, idx) => {
                                        const isGenre = item.type === 'genre';
                                        const isSignature = item.type === 'internet';
                                        const trayEl = isGenre ? (
                                            renderGenreTray({
                                                columnIdx: drawerColIdx,
                                                columnSelections,
                                                genrePackages: props.genrePackages,
                                                priceMap: props.priceMap,
                                                miniPackages: props.miniPackages,
                                                miniSelectionsByColumn,
                                                handleMiniPackChipClick,
                                                getEnabledMiniPackIds,
                                                multiPackages: props.multipackages
                                            })
                                        ) : isSignature ? (
                                            renderSignatureTray({
                                                columnIdx: drawerColIdx,
                                                columnSelections,
                                                packagesConfig: props.packagesConfig,
                                                priceMap: props.priceMap
                                            })
                                        ) : null;
                                        if (idx === Math.min(genreIdx, sigIdx) + 1) {
                                            return [
                                                <DtvBox key="or-separator" sx={{ ...pkgStyles.orSeparator }}>
                                                    <DtvBox sx={{ ...pkgStyles.orSeparatorLine }} />
                                                    <DtvText content="OR" variant="body2medium" sx={{ ...pkgStyles.orSeparatorText }} />
                                                    <DtvBox sx={{ ...pkgStyles.orSeparatorLine }} />
                                                </DtvBox>,
                                                trayEl
                                            ];
                                        }
                                        return trayEl;
                                    });
                                }
                                    return trays.map(item => {
                                        if (item.type === 'genre') {
                                            return (
                                                <React.Fragment key="genre">
                                                    {renderGenreTray({
                                                        columnIdx: drawerColIdx,
                                                        columnSelections,
                                                        genrePackages: props.genrePackages,
                                                        priceMap: props.priceMap,
                                                        miniPackages: props.miniPackages,
                                                        miniSelectionsByColumn,
                                                        handleMiniPackChipClick,
                                                        getEnabledMiniPackIds,
                                                        multiPackages: props.multipackages
                                                    })}
                                                </React.Fragment>
                                            );
                                        } else if (item.type === 'internet') {
                                            return (
                                                <React.Fragment key="signature">
                                                    {renderSignatureTray({
                                                        columnIdx: drawerColIdx,
                                                        columnSelections,
                                                        packagesConfig: props.packagesConfig,
                                                        priceMap: props.priceMap
                                                    })}
                                                </React.Fragment>
                                            );
                                        }
                                        return null;
                                    });
                            })()}
                        </DtvBox>
                    )}
                    <DtvBox
                        id="apct-section"
                        component="tr"
                    >
                        <DtvBox
                            component="td"
                            sx={{
                                width: '100%',
                                padding: '0 !important',
                                border: 'none',
                                backgroundColor: colorPalette?.neutral_white?.main
                            }}
                        >
                            {accordionsData?.map((section: any, index: number) => {
                                const isChannels =
                                    section?.accordionType === 'Channels' &&
                                    section?.nestedSections?.length > 0;
                                if (isChannels) {
                                    return (
                                        <React.Fragment key={`section-${index}`}>
                                            <DtvBox
                                                style={{
                                                    position: 'sticky',
                                                    top: headerHeight,
                                                    left: 0
                                                }}
                                                sx={{
                                                    minHeight: 48,
                                                    height: 48,
                                                    backgroundColor:
                                                        section?.accordionHeading?.backgroundColor ||
                                                        colorPalette.blue[50],
                                                    color:
                                                        section?.accordionHeading?.color || '#000',
                                                    padding: '12px 20px',
                                                    zIndex: 100
                                                }}
                                            >
                                                {section?.accordionHeading?.content}
                                            </DtvBox>
                                        </React.Fragment>
                                    );
                                }
                                /* Parent accordion */
                                return (
                                    <AccordionRows
                                        key={`parent-${index}`}
                                        index={index}
                                        section={section}
                                        displayPackages={displayPackages}
                                        activePackagesByColumn={props?.activePackagesByColumn}
                                        accLength={accordionsData.length}
                                        headerHeight={headerHeight}
                                        mainColWidthStyle={mainColWidthStyle}
                                        myfreeDtvDescription={myfreeDtvDescription}
                                        type="parent"
                                    />
                                );
                            })}
                        </DtvBox>
                    </DtvBox>
                </TableBody>
            </Table>
            <DtvSnackBar
                open={conflictSnackbarOpen}
                topPx={conflictSnackbarTopPx}
                miniPopupVal={[{
                    message: conflictSnackbarMessage,
                    backgroundColor: '#1C3A5B',
                    textColor: '#FFFFFF'
                }]}
            />
        </DtvBox>
    );
  } catch (error) {
    console.error("Error Customer Acq Compare Tool - packagecmprgrid >> ", error);
    return <ErrorDisplay error={error} component="Customer Acq Compare Tool -> packagecmprgrid"/>;
  }
}
// Generated by Copilot- End
